import moment from 'moment';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser, fetchCurrentWorkerUserExtraDetails } from '../../ducks/user.duck';
import { updatedEntities, denormalisedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { MIN_WORKER_START_HOUR } from '../../marketplace-custom-config';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 42 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
// const RESULT_PAGE_SIZE = 42;
const RESULT_PAGE_SIZE = 10;

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/BookAShiftListPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/BookAShiftListPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/BookAShiftListPage/FETCH_LISTINGS_ERROR';

export const FETCH_CURRENT_WORKER_REQUEST = 'app/BookAShiftListPage/FETCH_CURRENT_WORKER_REQUEST';
export const FETCH_CURRENT_WORKER_SUCCESS = 'app/BookAShiftListPage/FETCH_CURRENT_WORKER_SUCCESS';
export const FETCH_CURRENT_WORKER_ERROR = 'app/BookAShiftListPage/FETCH_CURRENT_WORKER_ERROR';

export const ADD_OWN_ENTITIES = 'app/BookAShiftListPage/ADD_OWN_ENTITIES';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  queryParams: null,
  queryInProgress: false,
  queryListingsError: null,
  currentPageResultIds: [],
  ownEntities: {},
  currentWorkerId: {},
  currentWorkerError: null,
  currentWorkerLoading: false,
};

export const addOwnEntities = response => ({
  type: ADD_OWN_ENTITIES,
  payload: { data: response.data },
});

const resultIds = data => data.data.map(l => l.id);

const bookAShiftListPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        queryInProgress: true,
        queryListingsError: null,
        currentPageResultIds: [],
      };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        queryInProgress: false,
      };
    case FETCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, queryInProgress: false, queryListingsError: payload };

    case FETCH_CURRENT_WORKER_REQUEST:
      return {
        ...state,
        currentWorkerLoading: true,
        currentWorkerError: null,
        currentPageResultIds: [],
      };
    case FETCH_CURRENT_WORKER_SUCCESS:
      return {
        ...state,
        currentWorkerId: payload.data.data.id,
        currentWorkerLoading: false,
        currentWorkerError: null,
      };
    case FETCH_CURRENT_WORKER_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, currentWorkerLoading: false, currentWorkerError: payload };

    case ADD_OWN_ENTITIES:
      return {
        ...state,
        ownEntities: updatedEntities(state.ownEntities, payload.data),
      };

    default:
      return state;
  }
};

export default bookAShiftListPageReducer;

// ================ Selectors ================ //

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds, authorId) => {
  const { ownEntities } = state.BookAShiftListPage;
  const resources = listingIds.map(id => ({
    id,
    type: 'ownListing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound);
};

// ================ Action creators ================ //

// This works the same way as addMarketplaceEntities,
// but we don't want to mix own listings with searched listings
// (own listings data contains different info - e.g. exact location etc.)

export const queryListingsRequest = queryParams => ({
  type: FETCH_LISTINGS_REQUEST,
  payload: { queryParams },
});

export const queryListingsSuccess = response => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const queryListingsError = e => ({
  type: FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const queryCurrentWorkerRequest = queryParams => ({
  type: FETCH_CURRENT_WORKER_REQUEST,
  payload: { queryParams },
});

export const queryCurrentWorkerSuccess = response => ({
  type: FETCH_CURRENT_WORKER_SUCCESS,
  payload: { data: response.data },
});

export const queryCurrentWorkerError = e => ({
  type: FETCH_CURRENT_WORKER_ERROR,
  error: true,
  payload: e,
});

// Throwing error for new (loadData may need that info)
export const queryOwnListings = queryParams => async (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest(queryParams));
  const { perPage, ...rest } = queryParams;
  await dispatch(fetchCurrentUser());
  const currentUser = getState().user.currentUser;
  const authorId = currentUser.id.uuid;
  const minWorkStartHour = MIN_WORKER_START_HOUR;
  const userOffset = moment().utcOffset();
  const currentDate = Math.round(
    moment
      .utc()
      .add(minWorkStartHour, 'hour')
      .add(userOffset, 'm')
      .valueOf() / 1000
  );
  const params = {
    ...rest,
    authorId,
    per_page: perPage,
    state: 'published',
    pub_isChatWithAdminListing: false,
    pub_gigStartDateInNumberFormat: `${currentDate},`,
  };
  return sdk.listings
    .query(params)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(queryListingsSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(queryListingsError(storableError(e)));
      throw e;
    });
};
export const fetchCurrentWorker = params => (dispatch, getState, sdk) => {
  const { id, listingSearchParams } = params;
  let apiResp;
  dispatch(queryCurrentWorkerRequest({ id }));
  return sdk.users
    .show(
      {
        id,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x', 'default'],
      },
      { expand: true }
    )
    .then(response => {
      apiResp = response;
      if (!response.data.data.attributes.profile.publicData?.isProvider) {
        return dispatch(fetchCurrentWorkerUserExtraDetails(response.data.data.id.uuid));
      }
      return null;
    })
    .then(response => {
      if (response !== null && typeof response === 'object') {
        Object.assign(apiResp.data.data.attributes.profile.publicData, response);
      }
      const jobCats = response.jobCategories.reduce((str, curr, idx) => {
        return idx === 0 ? str + `has_any:${curr.category}` : str + `, ${curr.category}`;
      }, '');
      const jobSubCats = response.jobCategories.reduce((str, curr, idx) => {
        return idx === 0 ? str + `has_any:${curr.subCategory}` : str + `, ${curr.subCategory}`;
      }, '');
      const par = { pub_searchJobsubCategory: jobSubCats, pub_searchJobcategory: jobCats };
      dispatch(queryOwnListings({ ...listingSearchParams, ...par }));
      dispatch(addMarketplaceEntities(apiResp));
      dispatch(queryCurrentWorkerSuccess(apiResp));
      return apiResp;
    })
    .catch(e => {
      dispatch(queryCurrentWorkerError(storableError(e)));
      throw e;
    });
};
export const loadData = (params, search) => (dispatch, getState, sdk) => {
  const queryParams = parse(search);
  const { id } = params;
  const page = queryParams.page || 1;
  return Promise.all([
    dispatch(
      fetchCurrentWorker({
        id,
        listingSearchParams: {
          ...queryParams,
          page,
          perPage: RESULT_PAGE_SIZE,
          include: ['images'],
          'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
          'limit.images': 1,
        },
      })
    ),
  ]);
};
